import { _IdGenerator } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { HdBodygraphPlanetaryActivation, HdBodygraphSide } from '../../hd-bodygraph.models';
import { HdChartHoverHighlightService } from '../../hd-chart-hover-highlight.service';
import { createHdGateHoverHighlight } from '../../hd-gate-hover-highlight';
import { HdContentLanguageService } from '../../hd-content-language.service';
import { HdPlanetInfoService } from '../../hd-planet-info.service';
import { HdFixingState, HdPlanetDisplayConfig } from '../hd-planet-display-config';
import { HdPlanetActivationBlockClickEvent } from '../hd-planet-activation-block/hd-planet-activation-block.component';

/**
 * Eén regel in de eenvoudige (classic) planeetgrid: planeetglyph + poort.lijn
 * + exaltatie/detriment-pijl. Deelt exact hetzelfde hover/klik-contract met de
 * {@link HdChartHoverHighlightService} als het uitgebreide
 * `hd-planet-activation-block`, zodat hover-highlight en gate-info klikken
 * identiek werken — zie `docs/human-design/hd-chart-hover-highlight.md`.
 * Dat geldt ook voor de HD-keynotes-toggle: de planeet-keynote verschijnt als
 * kaart boven de regel en de keynote-hover wordt gemeld zodat gekoppelde
 * keynotes (chart én grid tegelijk) ook vanuit de lijst werken.
 */
@Component({
  selector: 'hd-simple-planet-block',
  templateUrl: './hd-simple-planet-block.component.html',
  styleUrl: './hd-simple-planet-block.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    'role': 'button',
    'tabindex': '0',
    '(click)': 'emitBlockClicked($event)',
    '(keydown.enter)': 'emitBlockClicked($event)',
    '(keydown.space)': 'emitBlockClicked($event)',
    '(mouseenter)': 'onMouseEnter()',
    '(mouseleave)': 'onMouseLeave()',
    '(focus)': 'hostFocused.set(true)',
    '(blur)': 'hostFocused.set(false)',
    '[attr.data-gate]': 'activation().gate',
    '[attr.aria-describedby]': 'hdKeynoteVisible() ? hdKeynoteId : null',
    '[class.is-hover-match]': 'isHoverMatch()',
    '[class.is-hover-source]': 'isHoverSource()',
    '[class.is-hover-dimmed]': 'isHoverDimmed()',
  },
})
export class HdSimplePlanetBlockComponent {
  readonly activation = input.required<HdBodygraphPlanetaryActivation>();
  readonly side = input.required<HdBodygraphSide>();
  readonly displayConfig = input.required<HdPlanetDisplayConfig>();
  readonly fixingState = input<HdFixingState>('none');
  /** Toon de planeet-keynote als hover-/focuskaart boven de regel (shortcut "HD Keynotes"). */
  readonly showHdKeynotes = input(false);
  readonly blockClicked = output<HdPlanetActivationBlockClickEvent>();

  private readonly hoverService = inject(HdChartHoverHighlightService, { optional: true });
  private readonly contentLang = inject(HdContentLanguageService);
  private readonly planetInfoService = inject(HdPlanetInfoService);
  private readonly hostHovered = signal(false);
  protected readonly hostFocused = signal(false);

  protected readonly showExaltationArrow = computed(
    () => this.fixingState() === 'exaltation' || this.fixingState() === 'both',
  );
  protected readonly showDetrimentArrow = computed(
    () => this.fixingState() === 'detriment' || this.fixingState() === 'both',
  );

  /** Gedeelde hover-regels (match / bron / dim), zie {@link createHdGateHoverHighlight}. */
  private readonly gateHover = createHdGateHoverHighlight(this.hoverService, () => this.activation().gate, this.hostHovered);
  protected readonly isHoverMatch = this.gateHover.isMatch;
  protected readonly isHoverSource = this.gateHover.isSource;
  protected readonly isHoverDimmed = this.gateHover.isDimmed;

  /** Zelfde bron als de uitgebreide grid: de planeet-subtitle in de contenttaal. */
  protected readonly hdKeynote = computed<string | null>(() => {
    if (!this.showHdKeynotes()) return null;
    const subtitle = this.planetInfoService
      .planetInfoIn(this.displayConfig().key, this.contentLang.effectiveLang())
      .subtitle
      .trim();
    return subtitle || null;
  });

  /** Spiegel van `HdPlanetActivationBlockComponent.isMatchingKeynoteHover`. */
  private readonly isMatchingKeynoteHover = computed(() => {
    const keynoteHover = this.hoverService?.keynoteHover();
    if (keynoteHover?.source === 'planet-grid-block') {
      return keynoteHover.gate === this.activation().gate;
    }
    if (this.hoverService?.keynotesAtHoverOnly() ?? true) return false;
    if (keynoteHover?.source !== 'chart-gate') return false;
    if (keynoteHover.kind === 'channel') {
      return keynoteHover.gates().has(this.activation().gate);
    }
    return keynoteHover.gate === this.activation().gate;
  });

  protected readonly hdKeynoteVisible = computed(() =>
    (this.hostHovered() || this.hostFocused() || this.isMatchingKeynoteHover())
      && this.hdKeynote() !== null,
  );

  protected readonly hdKeynoteId = inject(_IdGenerator).getId('hd-simple-planet-keynote-');

  protected onMouseEnter(): void {
    this.hostHovered.set(true);
    this.hoverService?.setKeynoteHover(this.activation().gate, 'planet-grid-block');
    this.hoverService?.setGateHover(this.activation().gate, 'planet-grid-block');
  }

  protected onMouseLeave(): void {
    this.hostHovered.set(false);
    this.hoverService?.setKeynoteHover(null, 'planet-grid-block');
    this.hoverService?.setGateHover(null, 'planet-grid-block');
  }

  protected emitBlockClicked(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (event instanceof MouseEvent && (event.ctrlKey || event.metaKey)) {
      if (this.hoverService?.enabled()) {
        this.hoverService.toggleFreezeForTarget({ kind: 'gate', gate: this.activation().gate });
        return;
      }
    }
    const origin =
      event instanceof MouseEvent && (event.clientX !== 0 || event.clientY !== 0)
        ? { x: event.clientX, y: event.clientY }
        : { x: 0, y: 0 };
    this.blockClicked.emit({ activation: this.activation(), origin });
  }
}
